import React, { useState, useEffect } from "react";
import { useTranslation } from "../../_common/hooks/useTranslation";
import { useDispatch } from "react-redux";
import { SAGA_ACTIONS } from "../../_config";
import { Skill } from "../../_common/interfaces/ApiReqRes";

interface SkillSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  skillsData: Skill[];
  selectedSkills: string[];
  onSkillSelect: (skill: Skill) => void;
}

const SkillSelectionModal: React.FC<SkillSelectionModalProps> = ({
  isOpen,
  onClose,
  skillsData,
  selectedSkills,
  onSkillSelect,
}) => {
  const { t } = useTranslation("job-create");
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Debounced API call for skill search
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchTerm.trim() !== "") {
        setIsSearching(true);
        dispatch({
          type: SAGA_ACTIONS.COMMON.SKILLS,
          payload: { q: searchTerm.trim(), limit: 30, offset: 0 },
        });
        // Reset searching state after API call
        setTimeout(() => setIsSearching(false), 500);
      } else {
        // Fetch all skills when search is cleared
        dispatch({
          type: SAGA_ACTIONS.COMMON.SKILLS,
          payload: { limit: 30, offset: 0 },
        });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, isOpen, dispatch]);

  if (!isOpen) {
    return null;
  }

  const handleSkillSelect = (skill: Skill) => {
    onSkillSelect(skill);
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter out already selected skills
  const availableSkills = skillsData?.filter((skill: Skill) => !selectedSkills.includes(skill.name)) || [];

  return (
    <div className="currency-modal-overlay" onClick={onClose}>
      <div className="currency-modal" onClick={(e) => e.stopPropagation()}>
        <div className="currency-modal-header">
          <h3 className="currency-head">{t("jobCreate.skillModal.title")}</h3>
          <button className="close-modal" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="currency-search-box">
          <input
            type="text"
            className="currency-search-input"
            placeholder={t("jobCreate.skillModal.searchPlaceholder") || "Search skills..."}
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus
          />
        </div>

        <div className="currency-list">
          {isSearching ? (
            <div className="currency-loading">{t("jobCreate.skillModal.searching") || "Searching..."}</div>
          ) : availableSkills.length > 0 ? (
            availableSkills.map((skill: Skill) => (
              <div key={skill.id} className="currency-option" onClick={() => handleSkillSelect(skill)}>
                <div>
                  <div className="cur-name">{skill.name}</div>
                  {skill.category && <div className="cur-code">{skill.category}</div>}
                </div>
              </div>
            ))
          ) : (
            <div className="currency-no-results">
              {selectedSkills?.length > 0 && skillsData?.length > 0 && availableSkills?.length === 0
                ? t("jobCreate.skillModal.allSelected") || "All skills selected"
                : t("jobCreate.skillModal.noResults") || "No skills found"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillSelectionModal;
